import { getAnalytics, isSupported, logEvent, type Analytics } from 'firebase/analytics'
import { app } from 'src/shims/firebase'

let analyticsCache: Analytics | null | undefined

async function getWebAnalytics(): Promise<Analytics | null> {
  if (analyticsCache !== undefined) return analyticsCache
  if (typeof window === 'undefined') {
    analyticsCache = null
    return null
  }
  try {
    if (!(await isSupported())) {
      analyticsCache = null
      return null
    }
    analyticsCache = getAnalytics(app)
    return analyticsCache
  } catch {
    analyticsCache = null
    return null
  }
}

/**
 * Côté web, le SDK `firebase` public n’inclut pas Crashlytics ; l’événement Analytics `exception`
 * remonte dans GA4 / Firebase (onglet Analyse) pour les erreurs non fatales.
 */
export function reportWebNonFatalError(context: string, err: unknown): void {
  void getWebAnalytics().then((analytics) => {
    try {
      if (!analytics) return
      const e = err as { code?: string; message?: string }
      const description = [context, e?.code, e?.message ?? String(err)]
        .filter(Boolean)
        .join(' | ')
        .slice(0, 500)
      logEvent(analytics, 'exception', { description, fatal: false })
    } catch {
      /* Analytics ne doit jamais impacter l’app */
    }
  })
}

/**
 * Connexion OAuth (web) : journal console + événement GA4 `auth_debug` pour diagnostiquer
 * Auth OK mais profil / sync encas d’échec — ne lève jamais d’erreur.
 */
export function authWebLog(step: string, details?: Record<string, unknown>): void {
  if (import.meta.env.DEV) {
    try {
      const oneLine =
        details == null
          ? `[batiweb:auth] ${step}`
          : `[batiweb:auth] ${step} | ${JSON.stringify(details)}`
      // vConsole sur mobile affiche mieux une chaîne qu’un 2e argument objet
      if (typeof console !== 'undefined' && typeof console.log === 'function') {
        console.log(oneLine)
      }
      if (typeof console !== 'undefined' && typeof console.info === 'function') {
        console.info('[batiweb:auth]', step, details ?? '')
      }
    } catch {
      /* ignore */
    }
  }
  void getWebAnalytics().then((analytics) => {
    try {
      if (!analytics) return
      const detailStr =
        details == null
          ? ''
          : JSON.stringify(details, (_k, v) =>
              typeof v === 'string' && v.length > 96 ? `${v.slice(0, 96)}…` : v
            ).slice(0, 120)
      logEvent(analytics, 'auth_debug', {
        step: String(step).slice(0, 40),
        detail: detailStr,
      })
    } catch {
      /* Analytics ne doit jamais casser l’auth */
    }
  })
}
